import { parseError } from '@snapchat/core';

/** Random timeout to wait for the iframe with secproxy auth to load. */
const secProxyWaitTimeoutMs = 5_000;

/**
 * Whether to follow 301/307 redirects
 *
 * NOTE: By default, do not allow redirect; capture that case and attempt to refresh the session
 * cookies.
 */
let allowAuthRedirect = false;

/**
 * Set whether or not to allow auth redirects when making requests to *.sc-corp.net domains.
 *
 * If this is `false`, then there will be an error in the console about a `401` response.
 *
 * If this is `true` and redirects are opaque, then this code will assume that any 307 redirect is
 * an auth redirect, which isn't always the case.
 *
 * Default value is `false`.
 */
export function setAllowAuthRedirect(value: boolean): void {
  allowAuthRedirect = value;
}

/**
 * Helper fetch for calling fetch over *.sc-corp.net domains.
 *
 * What this does is if it detects a redirect or unauthorized response, will try to open the page in
 * an iframe to refresh the session cookies. Then retry the RPC.
 *
 * This solution isn't perfect, but should be
 */
export const corpFetch: typeof fetch = async (url, requestInit): Promise<Response> => {
  if (!requestInit) {
    return fetch(url, requestInit);
  }

  // Required for secproxy requests. Attaches cookies to the request.
  requestInit.credentials = 'include';

  if (!allowAuthRedirect) {
    // Stops following 307 redirects.
    // So we can detect redirects before they throw exceptions in the console.
    requestInit.redirect = 'manual';
  } else if (typeof requestInit.headers === 'object') {
    // Makes it throw 401 instead of 307.
    (requestInit.headers as Record<string, string>)['X-Requested-With'] = 'XMLHttpRequest';
  }

  let response = await fetch(url, requestInit);

  // If we hit a redirect, open an iframe to refresh the session cookies.
  if (
    response.status === 401 || // Unauthorized.
    response.status === 307 || // Redirecting to (presumably) auth page.
    response.type === 'opaqueredirect' // Redirecting to (presumably) assume auth page.
  ) {
    await new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      const urlInput = typeof url === 'object' && 'url' in url ? url.url : url;
      const src = new URL(urlInput);
      src.searchParams.set('gcp-iap-mode', 'DO_SESSION_REFRESH');

      iframe.addEventListener('load', () => {
        resolve(null);
        iframe.remove(); // Delete iframe after it's done being loaded.
      });
      iframe.addEventListener('error', event => {
        reject(parseError(event.error ?? event.message));
        iframe.remove(); // Delete iframe after it's done being loaded.
      });
      iframe.src = src.href;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      setTimeout(() => {
        if (!iframe.parentNode) return; // Do nothing if detatched.
        const message =
          'Timed out waiting for session cookie iframe to load. Are third-party cookies blocked?';
        reject(new Error(message));
      }, secProxyWaitTimeoutMs);
    });

    // Try the request again with refreshed cookies.
    response = await fetch(url, requestInit);

    // Sanity check: When third-party cookies are blocked, we get into a state
    // where auth cookies do not get set, so we cannot make a request through
    // SecProxy at all. In this case it's useful to throw an error that's
    // mode descriptive.
    if (response.type === 'opaqueredirect') {
      const urlInput = url instanceof URL ? url.href : typeof url === 'string' ? url : url.url;
      throw new Error(
        `Failed to refresh session cookies for ${urlInput}. Are third-party cookies blocked?`
      );
    }
  }

  return response;
};
